import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'

// Mirrors backend/schemas/kh_catalog.py. Defined locally — these endpoints
// aren't in generated.ts (KH catalog is post-gen, same as Session 11 surfaces).
export interface KhCatalogItem {
  sku: string
  ean: string | null
  name_en: string | null
  model: string | null
  color: string | null
  size: string | null
  variant_description: string | null
  price_cents: number
  stock_available: number
  end_of_life: boolean
  season: string | null
  main_group: string | null
  sub_group_1: string | null
  sub_group_2: string | null
  primary_image_url: string | null
  in_shopify: boolean
  shopify_product_id: string | null
  shopify_variant_id: string | null
  first_seen_at: string | null
  last_synced_at: string | null
}

export interface KhCatalogListResponse {
  items: KhCatalogItem[]
  total: number
  page: number
  page_size: number
}

export interface KhCatalogFacets {
  main_groups: string[]
  sub_groups: string[]
  seasons: string[]
}

export type KhSort = 'newest' | 'stock_desc' | 'name_asc'

export interface KhCatalogFilters {
  search?: string
  main_group?: string
  sub_group?: string
  season?: string
  in_shopify?: boolean // undefined = all
  sort: KhSort
  page: number
  page_size: number
}

export interface AddToShopifyResponse {
  sku: string
  status: 'created' | 'already_in_shopify'
  shopify_product_id: string | null
  shopify_variant_id: string | null
  in_shopify: boolean
  message: string
}

export function useKhCatalog(filters: KhCatalogFilters) {
  return useQuery({
    queryKey: ['kh-catalog', filters] as const,
    queryFn: () =>
      apiFetch<KhCatalogListResponse>('GET', '/api/kh-catalog/', {
        query: {
          search: filters.search || undefined,
          main_group: filters.main_group || undefined,
          sub_group: filters.sub_group || undefined,
          season: filters.season || undefined,
          in_shopify: filters.in_shopify === undefined ? undefined : String(filters.in_shopify),
          sort: filters.sort,
          page: filters.page,
          page_size: filters.page_size,
        },
      }),
    staleTime: 30_000,
    // Keep the previous page visible while the next one loads (no flash-to-empty).
    placeholderData: (prev) => prev,
  })
}

export function useKhCatalogFacets() {
  return useQuery({
    queryKey: ['kh-catalog-facets'] as const,
    queryFn: () => apiFetch<KhCatalogFacets>('GET', '/api/kh-catalog/facets'),
    staleTime: 5 * 60_000,
  })
}

export function useAddToShopify() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sku: string) =>
      apiFetch<AddToShopifyResponse>('POST', '/api/kh-catalog/add-to-shopify', { body: { sku } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kh-catalog'] })
    },
  })
}
